/**
 * "What happened" (ADR-0092): one request, the run's own record joined with its signed audit
 * log — `GET /v1/explain/runs/{id}/what-happened` and the auditors' evidence pack
 * `GET /v1/explain/runs/{id}/evidence`. Content (question, answer, step data, comments,
 * passages) is present only for readers OPA allows (`explain.content`), masked.
 */
import type { LocalizedText } from "./types";
import type { AuditViewModel, RequestMode, RequestStatus, RequestStory, StoryReason, StoryStep, StoryView } from "./story";

export interface WhStepData {
  node: string;
  kind: "input" | "output";
  key: string;
  value: unknown;
  size?: number;
  truncated?: boolean;
  masked?: boolean;
  at?: string | null;
}

export interface WhPassage {
  title: string | null;
  collection: string | null;
  version: string | null;
  passage: number | null;
  score: number | null;
  snippet: string | null;
}

/** A story step folded into one of the agent's steps (plus what this view adds). */
export interface WhEvent extends StoryStep {
  /** An approver's comment (content). */
  comment?: string;
  /** What was approved: the call (content), and whether what ran matches the signed digest. */
  subject?: { tool: string | null; action: string | null; args: unknown };
  subject_check?: "match" | "differs" | "masked";
}

export type WhBadge = "checked" | "personal_data_hidden" | "check_unavailable" | "blocked";

/** One time the agent went through one of its steps. */
export interface WhStep {
  id: string;
  node: string;
  type: string | null;
  label: LocalizedText | null;
  visit: number;
  visits: number;
  /** The "several at once" step this one ran beside others in. */
  branch: string | null;
  at: string | null;
  duration_ms: number | null;
  outcome: string;
  events: WhEvent[];
  checks: WhEvent[];
  badges: WhBadge[];
  cost_usd: number;
  tokens: { input: number; output: number };
  failure_route: { to: string; label: LocalizedText | null } | null;
  child_run: string | null;
  passages: WhPassage[];
  data: { input: WhStepData[]; output: WhStepData[] } | null;
  /** A part of the run the platform had to try again (the AI model or a service did not answer). */
  tries?: { count: number; reasons: string[] } | null;
  /** A system step that did not work, and worked when the agent came back to it. */
  worked_later?: { visit: number; id: string } | null;
  /** How long the AI model worked in this step (for a team member: inside the member). */
  model_ms?: number;
  /** Another agent's step (team member, sub-agent): how often it had to try again inside, and why. */
  member_tries?: { retries: number; reasons: string[] } | null;
  /** ADR-0108: the data level an AI step was given its data at and the kind of model that allows. */
  data_level?: WhDataLevel | null;
}

export interface WhDataLevel {
  level: "public" | "internal" | "confidential" | "restricted" | string;
  ceiling: string | null;
  route: "online" | "local";
  because: { kind: "question" | "history" | "attachments" | "memory" | "earlier_steps" | "step" | "value" | string; node?: string; label?: LocalizedText | null; name?: string }[];
}

export interface WhChange {
  node: string | null;
  label: LocalizedText | null;
  tool: string | null;
  action: string | null;
  outcome: "changed" | "simulated" | "failed" | "refused" | string;
  undone: boolean;
  /** How many times the call was tried in all, and the try that worked. */
  tries?: number;
  worked_on?: number;
}

export interface WhGraphNode {
  id: string;
  type: string | null;
  label: LocalizedText | null;
  visits: number;
  outcome: string | null;
  attempts: number;
}

export interface WhGraphEdge {
  from: string;
  to: string;
  when?: string | null;
  kind?: "error" | "limit";
  taken: boolean;
}

export interface WhProofRow {
  seq: number;
  hash: string | null;
  prev_hash: string | null;
  event_type: string;
  kind: string | null;
  at: string | null;
  from: string | null;
  to: string | null;
  step_id: string | null;
  correlated: boolean;
  recorded_at: string | null;
  payload: Record<string, unknown>;
}

export interface WhProof extends Omit<AuditViewModel, "records"> {
  records: WhProofRow[];
  views: StoryView[];
}

export interface WhatHappened {
  run_id: string;
  tenant?: string | null;
  status: RequestStatus;
  mode: RequestMode;
  /** Where it ran (dev, test, staging, prod); null for runs before it was recorded. */
  environment?: string | null;
  reason: StoryReason | null;
  started_at: string | null;
  finished_at: string | null;
  duration_ms: number | null;
  requester: string | null;
  agent: RequestStory["agent"];
  came_in: { channel: string | null; trigger: string | null; conversation: boolean };
  card: {
    question: string | null;
    answer: string | null;
    content: "shown" | "hidden" | "unavailable";
    cost_usd: number | null;
    input_tokens: number | null;
    output_tokens: number | null;
    steps: number | null;
  };
  changes: WhChange[];
  problems: { node: string | null; tool: string | null; action: string | null; kind: string | null }[];
  steps: WhStep[];
  /** For-each steps (ADR-0104): each item's outcome and its own run. */
  loops?: { node: string; label: LocalizedText | null; total: number; finished: boolean; done: number; failed: number; not_run: number; items: { index: number; status: string | null; run_id: string | null; error: string | null }[] }[];
  /** Records before the agent's first step (or not tied to a step). */
  loose: WhEvent[];
  /** After the answer: keeping what the person said in memory (done, failed or nothing to keep). */
  after?: WhEvent[];
  end: StoryStep | null;
  correction: { replaced_by: string | null; replaces: string | null };
  graph: { available: boolean; entry: string | null; nodes: WhGraphNode[]; edges: WhGraphEdge[]; path: string[] };
  record_available: boolean;
  children: WhatHappened[];
  story?: RequestStory;
  access?: { content: boolean; audit_view: boolean; builder: boolean; can_ask: boolean };
  proof?: WhProof | null;
}

/** A child run listed only by reference: its full record is elsewhere in the same response
 * (a team inside a team lists its members' runs once, at the level above). */
interface WhLinkedRun {
  run_id: string;
  linked: true;
}

const isLinked = (c: WhatHappened | WhLinkedRun): c is WhLinkedRun => (c as WhLinkedRun).linked === true && !("agent" in c);

/**
 * Replace each linked child with the full record it names (found anywhere in the response), and
 * drop links that name nothing, so every screen sees complete runs only. A run already on the
 * path is not nested again.
 */
export function resolveLinkedRuns(view: WhatHappened): WhatHappened {
  const full = new Map<string, WhatHappened>();
  const collect = (v: WhatHappened) => {
    for (const c of (v.children ?? []) as (WhatHappened | WhLinkedRun)[]) {
      if (isLinked(c)) continue;
      if (!full.has(c.run_id)) full.set(c.run_id, c);
      collect(c);
    }
  };
  collect(view);
  const rebuild = (v: WhatHappened, path: Set<string>): WhatHappened => {
    const kids: WhatHappened[] = [];
    for (const c of (v.children ?? []) as (WhatHappened | WhLinkedRun)[]) {
      const real = isLinked(c) ? full.get(c.run_id) : c;
      if (!real || path.has(real.run_id)) continue;
      kids.push(rebuild(real, new Set([...path, real.run_id])));
    }
    return { ...v, children: kids };
  };
  return rebuild(view, new Set([view.run_id]));
}
